import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CreateAnnouncementDto } from '../dtos/create-announcement-dto.interface';
import { GetAnnouncementDto } from '../dtos/get-announcement-dto.interface';
import { UpdateAnnouncementDto } from '../dtos/update-announcement-dto.interface';
import { AumicoCoreProvider } from '../provider/aumico.core.provider';
import { RoutesUtil } from '../utils/routes.util';

@Injectable({
    providedIn: 'root',
})
export class AnnouncementsService {
    constructor(
        private readonly http: HttpClient,
        private readonly coreProvider: AumicoCoreProvider,
        private readonly routesUtil: RoutesUtil,
    ) {}

    /**
     * Get all announcements (admin/management view)
     */
    getAllAnnouncements$(): Observable<GetAnnouncementDto[]> {
        const url = this.routesUtil.buildAnnouncementsUrl();
        this.coreProvider.dispatchBlockingRequest('getAllAnnouncements$');
        return this.http
            .get<GetAnnouncementDto[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getAllAnnouncements$')));
    }

    /**
     * Create a new announcement
     */
    createAnnouncement$(announcement: CreateAnnouncementDto): Observable<GetAnnouncementDto> {
        const url = this.routesUtil.buildAnnouncementsUrl();
        this.coreProvider.dispatchBlockingRequest('createAnnouncement$');
        return this.http
            .post<GetAnnouncementDto>(url, announcement)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('createAnnouncement$')));
    }

    /**
     * Update an existing announcement
     */
    updateAnnouncement$(id: string, changes: UpdateAnnouncementDto): Observable<GetAnnouncementDto> {
        const url = this.routesUtil.buildAnnouncementByIdUrl(id);
        this.coreProvider.dispatchBlockingRequest('updateAnnouncement$');
        return this.http
            .patch<GetAnnouncementDto>(url, changes)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('updateAnnouncement$')));
    }

    /**
     * Delete an announcement
     */
    deleteAnnouncement$(id: string): Observable<GetAnnouncementDto> {
        const url = this.routesUtil.buildAnnouncementByIdUrl(id);
        this.coreProvider.dispatchBlockingRequest('deleteAnnouncement$');
        return this.http
            .delete<GetAnnouncementDto>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('deleteAnnouncement$')));
    }

    /**
     * Get announcements for the current user
     */
    getUserAnnouncements$(): Observable<GetAnnouncementDto[]> {
        const url = this.routesUtil.buildUserAnnouncementsUrl();
        this.coreProvider.dispatchBlockingRequest('getUserAnnouncements$');
        return this.http
            .get<GetAnnouncementDto[]>(url)
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('getUserAnnouncements$')));
    }

    /**
     * Dismiss an announcement for the current user
     */
    dismissAnnouncement$(announcementId: string): Observable<void> {
        const url = this.routesUtil.buildDismissAnnouncementUrl(announcementId);
        this.coreProvider.dispatchBlockingRequest('dismissAnnouncement$');
        return this.http
            .post<void>(url, {})
            .pipe(finalize(() => this.coreProvider.dispatchBlockingRequestEnd('dismissAnnouncement$')));
    }
}
